import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  signal,
  computed,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, combineLatest, fromEvent, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';

import { StageSizeType } from '../../performer-participant/performer-participant.types';
import { BrowserService } from '../../services/browser.service';
import { socialAppSelectors } from '../../state/app';

import { DrawerAnimation } from './animation';
import { FullScreenService } from '../../services/fullscreen-service';

const AUTO_CLOSE_HEIGHT = 650;

@Component({
  selector: 'app-presentation-drawer',
  templateUrl: './presentation-drawer.component.html',
  styleUrls: ['./presentation-drawer.component.scss'],
  animations: [DrawerAnimation],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationDrawerComponent extends Loggable implements OnDestroy {
  private store = inject(Store);
  private browserService = inject(BrowserService);
  protected fullScreenService = inject(FullScreenService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  public readonly isDrawerOpen = signal<boolean>(true);
  public readonly displayEarlyAccess = signal<boolean>(false);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private static instance?: PresentationDrawerComponent;

  public readonly isMobileFullscreen = signal<boolean>(false);

  // Feature flag as a signal
  public readonly presentationV3 = signal<boolean>(false);

  // Computed property to determine if the drawer should be shown
  public readonly shouldShowDrawer = computed(() => {
    return this.isDrawerOpen() && !this.isMobileFullscreen();
  });

  public constructor() {
    super();
    this.setDisplayName('PresentationDrawerComponent');
    PresentationDrawerComponent.instance = this;

    this.isMobileFullscreen.set(this.fullScreenService.isMobileFullscreen());

    this.browserService
      .checkBrowserAvailability()
      .then((check) => {
        this.displayEarlyAccess.set(check.isAvailable && check.earlyAccess);
      })
      .catch((err) => this.error(err));

    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.autoCloseGallery(window.innerHeight);
        this.changeDetectorRef.markForCheck();
      });

    combineLatest([
      getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3),
      this.store.select(socialAppSelectors.getPresentationStageSize),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([presentation_v3, stageSize]) => {
        this.presentationV3.set(presentation_v3);
        if (this.presentationV3()) {
          this.toggleDrawer(stageSize !== StageSizeType.FULL_SCREEN);
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  public static getInstance(): PresentationDrawerComponent | undefined {
    return this.instance;
  }

  public toggleDrawer(value?: boolean): void {
    if (value === undefined) {
      // Toggle the current value
      this.isDrawerOpen.update((current) => !current);
    } else {
      // Set to the specified value
      this.isDrawerOpen.set(value);
    }
  }

  private autoCloseGallery(screenHeight: number): void {
    if (screenHeight <= AUTO_CLOSE_HEIGHT && this.isDrawerOpen()) {
      this.toggleDrawer(false);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    PresentationDrawerComponent.instance = undefined;
    this.destroyed$.complete();
  }
}
